import { AwarenessChannel, type CanvasAwareness } from '@canvas/model/awareness';

import {
  adaptiveBatchIntervalPolicy,
  getAdaptiveBatchIntervalMs,
} from './adaptive-batch-interval.ts';

type AwarenessBatch = Map<AwarenessChannel, Set<number>>;

const AWARENESS_CHANNELS = Object.values(AwarenessChannel).filter(
  (channel): channel is AwarenessChannel => typeof channel === 'number',
);

export const awarenessSchedulePolicy = adaptiveBatchIntervalPolicy;
export const getAwarenessIntervalMs = getAdaptiveBatchIntervalMs;

export class AdaptiveAwarenessScheduler {
  private pending: AwarenessBatch = new Map();
  private lastSendAt: number | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private sending = false;
  private connected = false;
  private destroyed = false;

  public constructor(
    private readonly awareness: Pick<CanvasAwareness, 'getAwareness'>,
    private readonly send: (batch: AwarenessBatch) => void,
  ) {}

  public addUpdate(channel: AwarenessChannel, clientIds: Iterable<number>): void {
    if (this.destroyed || !this.connected) {
      return;
    }

    let dirty = this.pending.get(channel);
    for (const clientId of clientIds) {
      if (!dirty) {
        dirty = new Set();
        this.pending.set(channel, dirty);
      }
      dirty.add(clientId);
    }

    this.schedule();
  }

  public synchronize(
    initialState: ReadonlyMap<AwarenessChannel, ReadonlySet<number>>,
  ): void {
    if (this.destroyed) {
      return;
    }

    this.disconnect();
    this.connected = true;
    for (const [channel, clientIds] of initialState) {
      if (clientIds.size > 0) {
        this.pending.set(channel, new Set(clientIds));
      }
    }
    this.schedule();
  }

  public flush(): void {
    if (this.destroyed || !this.connected || this.sending) {
      return;
    }

    this.cancelTimer();
    this.sendPending();
  }

  public disconnect(): void {
    this.connected = false;
    this.cancelTimer();
    this.pending.clear();
    this.lastSendAt = undefined;
  }

  public destroy(): void {
    this.disconnect();
    this.destroyed = true;
  }

  /**
   * Count at scheduling boundaries so frequent updates do not scan every channel.
   */
  private getClientCount(): number {
    const clients = new Set<number>();
    for (const channel of AWARENESS_CHANNELS) {
      const channelAwareness = this.awareness.getAwareness(channel);
      for (const clientId of channelAwareness.getStates().keys()) {
        clients.add(clientId);
      }
      if (channelAwareness.getLocalState() !== null) {
        clients.add(channelAwareness.clientID);
      }
    }
    return clients.size;
  }

  /**
   * Recheck membership when a timer fires because it may change while waiting.
   */
  private schedule(): void {
    if (
      this.pending.size === 0 ||
      !this.connected ||
      this.destroyed ||
      this.sending ||
      this.timer !== undefined
    ) {
      return;
    }

    const now = Date.now();
    const dueAt =
      this.lastSendAt === undefined
        ? now
        : this.lastSendAt + getAwarenessIntervalMs(this.getClientCount());

    if (dueAt <= now) {
      this.sendPending();
    } else {
      this.timer = setTimeout(() => {
        this.timer = undefined;
        this.schedule();
      }, dueAt - now);
    }
  }

  /**
   * Swap batches before sending so reentrant updates remain pending.
   */
  private sendPending(): void {
    if (this.pending.size === 0) {
      return;
    }

    const batch = this.pending;
    this.pending = new Map();
    this.lastSendAt = Date.now();
    this.sending = true;
    try {
      this.send(batch);
    } finally {
      this.sending = false;
      this.schedule();
    }
  }

  private cancelTimer(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
